import {
    CircleDollarSign, ClockAlert, Radio, RefreshCcwDot, TrendingUp,
} from 'lucide-react';
import {
    MetricDefinition,
    MetricGroupDefinition,
    QuestionDefinition,
    QuestionKey,
} from './types';

/** Scale for health score values (0 – max). */
export const HEALTH_SCORE_MAX = 100;

/** Labels and descriptions for each health metric key returned by the API. */
export const METRIC_DEFINITIONS: Record<string, MetricDefinition> = {
    health_score: {
        label: 'Business Health',
        description: 'Weighted score representing overall business health',
        format: 'score',
    },
    sales_performance_score: {
        label: 'Sales Performance',
        shortLabel: 'Sales',
        description: 'Measures the win rate (deals closed vs deals lost) penalized by the proportion of deals that went cold without a decision.',
        format: 'score',
    },
    pipeline_growth_score: {
        label: 'Pipeline Growth',
        shortLabel: 'Growth',
        description: 'Measures whether new sales opportunities are growing compared to the previous period and a baseline growth rate expectation.',
        format: 'score',
    },
    returning_customers_score: {
        label: 'Returning Customers',
        description: 'Measures the percentage of past customers who opened a new opportunity this period, compared to a baseline return rate.',
        format: 'score',
    },
    channel_performance_score: {
        label: 'Channel Performance',
        shortLabel: 'Channels',
        description: 'Measures the average win rate across all active sales channels compared to the baseline win rate.',
        format: 'score',
    },
    stuck_opportunities_score: {
        label: 'Stuck Opportunities',
        description: 'Measures the proportion of open deals that are stuck without movement, relative to deals successfully closed this period. More stuck deals relative to wins means a lower score.',
        format: 'score',
        goodWhen: 'negative',
    },
    touch_points_per_lead: {
        label: 'Conversations per Lead',
        description: 'Customer conversations divided by new opportunities. Below 1.0: some leads never initiated contact. Above 1.0: leads are reaching out more than once.',
        format: 'none',
    },
    touch_points_per_sale: {
        label: 'Conversations per Sale',
        description: 'How many times customers initiated contact before purchasing. Rising values indicate customers need more conversations before buying.',
        format: 'none',
        goodWhen: 'negative',
    },
    touch_points_total: {
        label: 'Total Conversations',
        description: 'Customer-initiated conversations. Total spans all channels.',
        format: 'integer',
    },
    touch_points: {
        label: 'Customer Conversations',
        description: 'Customer-initiated conversations in this channel.',
        format: 'integer',
    },
    customers_return_rate: {
        label: 'Customers Return Rate',
        description: 'Percentage of eligible contacts who returned.',
        format: 'percentage',
        goodWhen: 'positive',
    },
    customers_returning_contacts: {
        label: 'Returning Contacts',
        description: 'Eligible contacts who opened a new opportunity during this period.',
        format: 'integer',
    },
    customers_eligible_contacts: {
        label: 'Eligible Contacts',
        description: 'Contacts with at least one previously closed opportunity (won or lost) who could potentially return.',
        format: 'integer',
    },
    pipeline_growth_rate: {
        label: 'Pipeline Growth Rate',
        description: 'Growth rate of new opportunities compared to the previous period.',
        format: 'percentage',
    },
    new_opportunities: {
        label: 'New Opportunities',
        shortLabel: 'New leads',
        description: 'New opportunities opened during this period.',
        format: 'integer',
    },
    stuck_opportunities: {
        label: 'Stuck Opportunities',
        description: 'Open opportunities with no stage movement beyond the configured stall threshold.',
        format: 'integer',
        goodWhen: 'negative',
    },
    total_revenue_at_risk: {
        label: 'Total Revenue at Risk',
        description: 'Total revenue potentially at risk due to open opportunities that may not reach closed-won status.',
        format: 'currency',
        goodWhen: 'negative',
    },
    opportunities_won: {
        label: 'Won Opportunities',
        description: 'Opportunities that reached closed-won status during each period.',
        format: 'integer',
    },
    opportunities_lost: {
        label: 'Lost Opportunities',
        description: 'Total number of opportunities that are either closed or presumed lost during each period.',
        format: 'integer',
        goodWhen: 'negative',
    },
    opportunities_closed_lost: {
        label: 'Confirmed Lost Opportunities',
        description: 'Opportunities explicitly moved to a closed-lost status during each period.',
        format: 'integer',
        goodWhen: 'negative',
    },
    opportunities_presumed_lost: {
        label: 'Presumed Lost Opportunities',
        description: 'Open opportunities with no activity beyond the inactivity threshold. These were never explicitly closed but are considered effectively lost.',
        format: 'integer',
        goodWhen: 'negative',
    },
    win_rate: {
        label: 'Win Rate',
        shortLabel: 'Win Rate',
        description: 'Percentage of opportunities that reached closed-won status compared to total opportunities opened.',
        format: 'percentage',
    },
    average_revenue_per_sale: {
        label: 'Average Revenue per Sale',
        description: 'Average revenue per closed-won opportunity.',
        format: 'currency',
    },
    total_revenue: {
        label: 'Total Revenue',
        description: 'Revenue from closed-won opportunities in the current period.',
        format: 'currency',
    },
};

export const METRICS_GROUPS_DEFINITIONS: Record<string, MetricGroupDefinition> = {
    touch_points: {
        label: 'Customer Conversations',
        description: 'Total inbound customer interactions across channels during this period.',
    },
    returning_customers: {
        label: 'Returning Customers',
        description: 'Customers who re-engaged by opening new opportunities in this period.',
    },
    pipeline_growth: {
        label: 'Pipeline Growth',
        description: 'Net increase in pipeline value from new and expanding opportunities.',
    },
    stuck_opportunities: {
        label: 'Stuck Opportunities',
        description: 'Opportunities with no recent progress, indicating risk of stagnation or loss.',
    },
    sales_performance: {
        label: 'Sales Performance',
        description: 'Overall effectiveness in converting opportunities into closed revenue.',
    },
    channel_performance: {
        label: 'Channel Performance',
        description: 'How each sales channel is converting opportunities compared to expectations.',
    },
};

export const QUESTION_DEFINITIONS: Partial<Record<QuestionKey, QuestionDefinition>> = {
    Q1: {
        scoreId: 'pipeline_growth_score',
        question: 'Is my business growing?',
        scoreComponent: 'pipeline_growth_score',
        metricsGroup: 'pipeline_growth',
        primaryMetrics: ['new_opportunities', 'pipeline_growth_rate'],
        drawerMetricKeys: [
            'new_opportunities',
            'pipeline_growth_rate',
        ],
        icon: TrendingUp,
    },
    Q2: {
        scoreId: 'sales_performance_score',
        question: 'Am I making enough sales?',
        metricsGroup: 'sales_performance',
        scoreComponent: 'sales_performance_score',
        primaryMetrics: ['total_revenue', 'win_rate'],
        drawerMetricKeys: [
            'total_revenue',
            'opportunities_won',
            'win_rate',
            'opportunities_lost',
        ],
        icon: CircleDollarSign,
    },
    Q4: {
        scoreId: 'returning_customers_score',
        question: 'Are customers comming back?',
        metricsGroup: 'returning_customers',
        scoreComponent: 'returning_customers_score',
        primaryMetrics: ['customers_returning_contacts', 'customers_return_rate'],
        drawerMetricKeys: [
            'customers_returning_contacts',
            'customers_return_rate',
            'customers_eligible_contacts',
        ],
        icon: RefreshCcwDot,
    },
    Q5: {
        scoreId: 'channel_performance_score',
        question: 'Are my sales channels working?',
        metricsGroup: 'channel_performance',
        scoreComponent: 'channel_performance_score',
        primaryMetricsPresentation: 'per_channel',
        primaryMetrics: ['touch_points_total', 'touch_points_per_sale'],
        drawerMetricKeys: [
            'touch_points_total',
            'touch_points_per_sale',
            'touch_points_per_lead',
        ],
        icon: Radio,
    },
    Q6: {
        scoreId: 'stuck_opportunities_score',
        question: 'Am I leaving money on the table?',
        metricsGroup: 'stuck_opportunities',
        scoreComponent: 'stuck_opportunities_score',
        primaryMetrics: ['total_revenue_at_risk', 'stuck_opportunities'],
        drawerMetricKeys: [
            'total_revenue_at_risk',
            'stuck_opportunities',
        ],
        icon: ClockAlert,
    },
};
